import { useState, useCallback, useMemo, useEffect, type FormEvent } from 'react';
import { apiClient, ApiClientError } from '../services/api-client';
import { validateExitInput, type ValidationError } from '@covered-calls/shared';
import type { BrokerProfile, ExitInput } from '@covered-calls/shared';
import {
  roundTo2dp,
  selectSchedule,
  computeCommission,
  Exchange,
  InstrumentClass,
} from '@covered-calls/shared';

/**
 * EarlyExitDialog — record buyback cost, sale price, exit date, and commissions
 * for closing a trade position. Displays realised P&L preview in real time.
 * Supports manual price entry when market data is unavailable.
 *
 * Validates: Requirements 6.4, 6.5, 6.6, 6.7
 */

export interface EarlyExitDialogProps {
  positionId: string;
  ticker: string;
  exchange: Exchange;
  entryPrice: number;
  quantity: number;
  premium: number;
  totalCommissions: number;
  /**
   * Whether the position currently has an ACTIVE short option (covered call)
   * that must be bought back on exit. When false (e.g. a protective-put or
   * plain stock position with no short call), the option-buyback cost and
   * commission are irrelevant: those fields are hidden, default to 0, and are
   * excluded from the commission auto-calc and the P&L preview. Defaults to
   * true to preserve the original covered-call exit behaviour.
   */
  hasActiveOption?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

/** Commission/GST split for a single exit leg, resolved from the shared calculation. */
interface CommissionSplit {
  commission: number;
  gst: number;
  total: number;
}

interface FormState {
  exitDate: string;
  exitStockPrice: string;
  exitOptionBuybackCost: string;
  stockSaleCommission: string;
  optionBuybackCommission: string;
  brokerProfileId: string;
  isManualPrice: boolean;
}

const initialFormState: FormState = {
  exitDate: new Date().toISOString().split('T')[0],
  exitStockPrice: '',
  exitOptionBuybackCost: '',
  stockSaleCommission: '',
  optionBuybackCommission: '',
  brokerProfileId: '',
  isManualPrice: false,
};

export function EarlyExitDialog({
  positionId,
  ticker,
  exchange,
  entryPrice,
  quantity,
  premium,
  totalCommissions,
  hasActiveOption = true,
  onSuccess,
  onCancel,
}: EarlyExitDialogProps) {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [brokerProfiles, setBrokerProfiles] = useState<BrokerProfile[]>([]);
  const [marketDataUnavailable, setMarketDataUnavailable] = useState(false);
  // Commission/GST split per exit leg from the shared calculation (null = not resolved).
  const [stockSaleSplit, setStockSaleSplit] = useState<CommissionSplit | null>(null);
  const [optionBuybackSplit, setOptionBuybackSplit] = useState<CommissionSplit | null>(null);
  // True when a broker profile is selected but a leg has no matching schedule (manual fallback).
  const [scheduleFallback, setScheduleFallback] = useState(false);

  // Fetch broker profiles on mount
  useEffect(() => {
    async function loadProfiles() {
      try {
        const profiles = await apiClient.get<BrokerProfile[]>('/broker-profiles');
        setBrokerProfiles(profiles);
      } catch {
        // Non-critical: continue without profiles
      }
    }
    loadProfiles();
  }, []);

  /**
   * Real-time P&L preview calculation.
   * Formula: (premium * qty) + ((salePrice - entryPrice) * qty) - (buybackCost * qty) - totalCommissions - exitCommissions
   */
  const pnlPreview = useMemo(() => {
    const salePrice = parseFloat(form.exitStockPrice);
    // With no active short option there is nothing to buy back, so treat the
    // buyback cost (and its commission) as zero rather than requiring input.
    const buybackCost = hasActiveOption ? parseFloat(form.exitOptionBuybackCost) : 0;
    const stockCommission = parseFloat(form.stockSaleCommission) || 0;
    const optionCommission = hasActiveOption ? (parseFloat(form.optionBuybackCommission) || 0) : 0;

    if (isNaN(salePrice) || isNaN(buybackCost)) {
      return null;
    }

    const premiumIncome = premium * quantity;
    const stockGainLoss = (salePrice - entryPrice) * quantity;
    const buybackTotal = buybackCost * quantity;
    const exitCommissions = stockCommission + optionCommission;
    const netPnL = premiumIncome + stockGainLoss - buybackTotal - totalCommissions - exitCommissions;

    return {
      premiumIncome: roundTo2dp(premiumIncome),
      stockGainLoss: roundTo2dp(stockGainLoss),
      buybackTotal: roundTo2dp(buybackTotal),
      existingCommissions: roundTo2dp(totalCommissions),
      exitCommissions: roundTo2dp(exitCommissions),
      netPnL: roundTo2dp(netPnL),
    };
  }, [form.exitStockPrice, form.exitOptionBuybackCost, form.stockSaleCommission, form.optionBuybackCommission, premium, quantity, entryPrice, totalCommissions, hasActiveOption]);

  const handleFieldChange = useCallback(
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => prev.filter((err) => err.field !== field));
      setServerError(null);
    },
    []
  );

  // Auto-populate commissions when broker profile is selected, resolving each
  // exit leg through the shared exchange-keyed schedule model.
  // Stock sale leg -> InstrumentClass.SHARES; option buyback leg -> InstrumentClass.OPTIONS.
  const handleBrokerProfileChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const profileId = e.target.value;
      setForm((prev) => ({ ...prev, brokerProfileId: profileId }));

      if (!profileId) {
        setStockSaleSplit(null);
        setOptionBuybackSplit(null);
        setScheduleFallback(false);
        setServerError(null);
        return;
      }

      const profile = brokerProfiles.find((p) => p.profileId === profileId);
      if (!profile) {
        setServerError(null);
        return;
      }

      let anyMissing = false;

      // Stock sale leg: tradeValue = exitStockPrice * quantity (SHARES).
      const salePrice = parseFloat(form.exitStockPrice) || 0;
      const stockSchedule = selectSchedule(profile, exchange, InstrumentClass.SHARES);
      let nextStockSplit: CommissionSplit | null = null;
      if (stockSchedule) {
        nextStockSplit = computeCommission(stockSchedule, {
          tradeValue: salePrice * quantity,
          quantity,
        });
      } else {
        anyMissing = true;
      }

      // Option buyback leg: tradeValue = exitOptionBuybackCost * quantity (OPTIONS).
      // Skipped entirely when the position has no active short option to buy back
      // (e.g. a protective-put exit) — there is no buyback trade, so no commission.
      const buybackCost = parseFloat(form.exitOptionBuybackCost) || 0;
      let nextOptionSplit: CommissionSplit | null = null;
      if (hasActiveOption) {
        const optionSchedule = selectSchedule(profile, exchange, InstrumentClass.OPTIONS);
        if (optionSchedule) {
          nextOptionSplit = computeCommission(optionSchedule, {
            tradeValue: buybackCost * quantity,
            quantity,
          });
        } else {
          anyMissing = true;
        }
      }

      setStockSaleSplit(nextStockSplit);
      setOptionBuybackSplit(nextOptionSplit);
      setScheduleFallback(anyMissing);

      // Populate each commission field with the effective total (commission + GST)
      // only for legs that have a matching schedule; leave others for manual entry.
      setForm((prev) => ({
        ...prev,
        stockSaleCommission: nextStockSplit ? nextStockSplit.total.toFixed(2) : prev.stockSaleCommission,
        optionBuybackCommission: nextOptionSplit ? nextOptionSplit.total.toFixed(2) : prev.optionBuybackCommission,
      }));

      setServerError(null);
    },
    [brokerProfiles, form.exitStockPrice, form.exitOptionBuybackCost, quantity, exchange, hasActiveOption]
  );

  const handleManualPriceToggle = useCallback(() => {
    setForm((prev) => ({ ...prev, isManualPrice: !prev.isManualPrice }));
    setMarketDataUnavailable(false);
  }, []);

  const getFieldError = (field: string): string | undefined => {
    return errors.find((err) => err.field === field)?.message;
  };

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setServerError(null);

      const input: ExitInput = {
        exitDate: form.exitDate,
        exitStockPrice: parseFloat(form.exitStockPrice),
        // No active short option -> no buyback trade: force cost and commission to 0.
        exitOptionBuybackCost: hasActiveOption ? parseFloat(form.exitOptionBuybackCost) : 0,
        stockSaleCommission: parseFloat(form.stockSaleCommission),
        optionBuybackCommission: hasActiveOption ? parseFloat(form.optionBuybackCommission) : 0,
        brokerProfileId: form.brokerProfileId || undefined,
      };

      // Client-side validation
      const validationErrors = validateExitInput(input);

      // Manual price entries must be positive (Req 6.7)
      if (form.isManualPrice) {
        if (input.exitStockPrice <= 0) {
          validationErrors.push({
            field: 'exitStockPrice',
            message: 'Manual stock sale price must be a positive number',
          });
        }
        if (input.exitOptionBuybackCost < 0) {
          validationErrors.push({
            field: 'exitOptionBuybackCost',
            message: 'Option buyback cost must be zero or positive',
          });
        }
      }

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors([]);
      setIsSubmitting(true);

      try {
        await apiClient.post(`/trades/${positionId}/close`, input);
        onSuccess();
      } catch (error) {
        if (error instanceof ApiClientError) {
          if (error.status === 502) {
            // Market data unavailable — offer manual entry (Req 6.6)
            setMarketDataUnavailable(true);
            setServerError(
              'Current market prices are unavailable. You can manually enter the stock sale price and option buyback cost to proceed.'
            );
            setForm((prev) => ({ ...prev, isManualPrice: true }));
          } else if (error.errors && error.errors.length > 0) {
            setErrors(error.errors.map((e) => ({ field: e.field, message: e.message })));
          } else {
            setServerError(error.message || 'An error occurred. Please try again.');
          }
        } else {
          setServerError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, positionId, onSuccess]
  );

  return (
    <div
      role="dialog"
      aria-labelledby="exit-dialog-title"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        <h2 id="exit-dialog-title" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          Exit Trade — {ticker}
        </h2>
        <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          Close your position of {quantity} shares at entry price ${entryPrice.toFixed(2)}.
        </p>

        {/* Manual price entry notice */}
        {(marketDataUnavailable || form.isManualPrice) && (
          <div
            role="status"
            style={{
              backgroundColor: 'var(--color-chip-warning-bg)',
              color: 'var(--color-chip-warning-text)',
              padding: '0.75rem 1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            Manual price entry mode — prices entered will be flagged as manually provided.
          </div>
        )}

        {serverError && (
          <div
            role="alert"
            style={{
              backgroundColor: 'var(--color-chip-danger-bg)',
              color: 'var(--color-chip-danger-text)',
              padding: '0.75rem 1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Exit Date */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="exit-date"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Exit Date
            </label>
            <input
              id="exit-date"
              type="date"
              value={form.exitDate}
              onChange={handleFieldChange('exitDate')}
              aria-invalid={!!getFieldError('exitDate')}
              aria-describedby={getFieldError('exitDate') ? 'exit-date-error' : undefined}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('exitDate') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('exitDate') && (
              <span id="exit-date-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                {getFieldError('exitDate')}
              </span>
            )}
          </div>

          {/* Stock Sale Price */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="exit-stock-price"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Stock Sale Price ($ per share)
            </label>
            <input
              id="exit-stock-price"
              type="number"
              step="0.01"
              min="0.01"
              max="999999.99"
              value={form.exitStockPrice}
              onChange={handleFieldChange('exitStockPrice')}
              aria-invalid={!!getFieldError('exitStockPrice')}
              aria-describedby={getFieldError('exitStockPrice') ? 'exit-stock-error' : undefined}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('exitStockPrice') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('exitStockPrice') && (
              <span id="exit-stock-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                {getFieldError('exitStockPrice')}
              </span>
            )}
          </div>

          {/* Option Buyback Cost — only relevant when a short option must be bought
              back on exit. Hidden (and treated as 0) for protective-put / no-call
              exits where there is nothing to buy back. */}
          {hasActiveOption && (
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="exit-buyback-cost"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Option Buyback Cost ($ per share)
            </label>
            <input
              id="exit-buyback-cost"
              type="number"
              step="0.01"
              min="0.00"
              max="999999.99"
              value={form.exitOptionBuybackCost}
              onChange={handleFieldChange('exitOptionBuybackCost')}
              aria-invalid={!!getFieldError('exitOptionBuybackCost')}
              aria-describedby={getFieldError('exitOptionBuybackCost') ? 'exit-buyback-error' : undefined}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('exitOptionBuybackCost') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('exitOptionBuybackCost') && (
              <span id="exit-buyback-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                {getFieldError('exitOptionBuybackCost')}
              </span>
            )}
          </div>
          )}

          {/* Broker Profile Selector */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="exit-broker-profile"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Broker Profile (optional)
            </label>
            <select
              id="exit-broker-profile"
              value={form.brokerProfileId}
              onChange={handleBrokerProfileChange}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--color-input-border)',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                backgroundColor: 'var(--color-surface)',
              }}
            >
              <option value="">-- Select broker profile --</option>
              {brokerProfiles.map((profile) => (
                <option key={profile.profileId} value={profile.profileId}>
                  {profile.brokerName} ({profile.schedules.length} schedule{profile.schedules.length === 1 ? '' : 's'})
                </option>
              ))}
            </select>
          </div>

          {/* Stock Sale Commission */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="exit-stock-commission"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Stock Sale Commission ($)
            </label>
            <input
              id="exit-stock-commission"
              type="number"
              step="0.01"
              min="0.00"
              max="99999.99"
              value={form.stockSaleCommission}
              onChange={handleFieldChange('stockSaleCommission')}
              aria-invalid={!!getFieldError('stockSaleCommission')}
              aria-describedby={getFieldError('stockSaleCommission') ? 'exit-stock-comm-error' : undefined}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('stockSaleCommission') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('stockSaleCommission') && (
              <span id="exit-stock-comm-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                {getFieldError('stockSaleCommission')}
              </span>
            )}
          </div>

          {/* Option Buyback Commission — hidden when there is no short option to
              buy back (no buyback trade means no buyback commission). */}
          {hasActiveOption && (
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="exit-option-commission"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Option Buyback Commission ($)
            </label>
            <input
              id="exit-option-commission"
              type="number"
              step="0.01"
              min="0.00"
              max="99999.99"
              value={form.optionBuybackCommission}
              onChange={handleFieldChange('optionBuybackCommission')}
              aria-invalid={!!getFieldError('optionBuybackCommission')}
              aria-describedby={getFieldError('optionBuybackCommission') ? 'exit-option-comm-error' : undefined}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('optionBuybackCommission') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('optionBuybackCommission') && (
              <span id="exit-option-comm-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                {getFieldError('optionBuybackCommission')}
              </span>
            )}
          </div>
          )}

          {/* Commission / GST split preview per exit leg (from shared calculation) */}
          {(stockSaleSplit || optionBuybackSplit) && (
            <div
              style={{
                backgroundColor: 'var(--color-surface-2)',
                borderRadius: '4px',
                padding: '1rem',
                marginBottom: '1rem',
                fontSize: '0.875rem',
              }}
            >
              <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
                Commission Breakdown
              </strong>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {stockSaleSplit && (
                    <>
                      <tr>
                        <td style={{ padding: '0.25rem 0' }}>Stock sale commission</td>
                        <td style={{ padding: '0.25rem 0', textAlign: 'right' }}>
                          ${stockSaleSplit.commission.toFixed(2)}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.25rem 0' }}>Stock sale GST</td>
                        <td style={{ padding: '0.25rem 0', textAlign: 'right' }}>
                          ${stockSaleSplit.gst.toFixed(2)}
                        </td>
                      </tr>
                    </>
                  )}
                  {optionBuybackSplit && (
                    <>
                      <tr>
                        <td style={{ padding: '0.25rem 0' }}>Option buyback commission</td>
                        <td style={{ padding: '0.25rem 0', textAlign: 'right' }}>
                          ${optionBuybackSplit.commission.toFixed(2)}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.25rem 0' }}>Option buyback GST</td>
                        <td style={{ padding: '0.25rem 0', textAlign: 'right' }}>
                          ${optionBuybackSplit.gst.toFixed(2)}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* No-schedule fallback note — do not crash, allow manual entry */}
          {scheduleFallback && (
            <div
              role="status"
              style={{
                backgroundColor: 'var(--color-chip-warning-bg)',
                color: 'var(--color-chip-warning-text)',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                marginBottom: '1rem',
                fontSize: '0.875rem',
              }}
            >
              No commission schedule found for one or more exit legs on this exchange.
              Enter the commission manually for the affected leg.
            </div>
          )}

          {/* Manual Price Entry Toggle */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: 'var(--color-muted)',
              }}
            >
              <input
                type="checkbox"
                checked={form.isManualPrice}
                onChange={handleManualPriceToggle}
                style={{ width: '16px', height: '16px' }}
              />
              Prices entered manually (market data unavailable)
            </label>
          </div>

          {/* Real-time P&L Preview */}
          {pnlPreview && (
            <div
              style={{
                backgroundColor: 'var(--color-surface-2)',
                borderRadius: '4px',
                padding: '1rem',
                marginBottom: '1.5rem',
                fontSize: '0.875rem',
              }}
            >
              <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
                Realised P&L Preview
              </strong>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '0.25rem 0' }}>Premium received</td>
                    <td style={{ padding: '0.25rem 0', textAlign: 'right' }}>
                      ${pnlPreview.premiumIncome.toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.25rem 0' }}>Stock gain/loss</td>
                    <td
                      style={{
                        padding: '0.25rem 0',
                        textAlign: 'right',
                        color: pnlPreview.stockGainLoss >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)',
                      }}
                    >
                      {pnlPreview.stockGainLoss >= 0 ? '+' : ''}${pnlPreview.stockGainLoss.toFixed(2)}
                    </td>
                  </tr>
                  {hasActiveOption && (
                  <tr>
                    <td style={{ padding: '0.25rem 0' }}>Option buyback cost</td>
                    <td style={{ padding: '0.25rem 0', textAlign: 'right', color: 'var(--color-pnl-negative)' }}>
                      -${pnlPreview.buybackTotal.toFixed(2)}
                    </td>
                  </tr>
                  )}
                  <tr>
                    <td style={{ padding: '0.25rem 0' }}>Existing commissions</td>
                    <td style={{ padding: '0.25rem 0', textAlign: 'right', color: 'var(--color-pnl-negative)' }}>
                      -${pnlPreview.existingCommissions.toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.25rem 0' }}>Exit commissions</td>
                    <td style={{ padding: '0.25rem 0', textAlign: 'right', color: 'var(--color-pnl-negative)' }}>
                      -${pnlPreview.exitCommissions.toFixed(2)}
                    </td>
                  </tr>
                  <tr
                    style={{
                      borderTop: '1px solid var(--color-border)',
                      fontWeight: 700,
                    }}
                  >
                    <td style={{ padding: '0.5rem 0 0' }}>Net P&L</td>
                    <td
                      style={{
                        padding: '0.5rem 0 0',
                        textAlign: 'right',
                        color: pnlPreview.netPnL >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)',
                        fontSize: '1rem',
                      }}
                    >
                      {pnlPreview.netPnL >= 0 ? '+' : ''}${pnlPreview.netPnL.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-muted)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-danger)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Closing Position...' : 'Close Position'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
